import { useEffect, useRef, useState } from 'react';
import { Clock, Pencil } from 'lucide-react';
import { formatDuration } from '../../utils/durationFormat';
import {
  getTopicAverageDurationMinutes,
  getTopicLastSessionDurations,
  isTopicDurationUnset,
  prefetchTopicDurationHistory,
  type TopicSessionDurationRecord,
} from '../../utils/topicDurationHistory';

interface TopicDurationFieldProps {
  topicId: string;
  /** Used for history lookup across sessions */
  topicTitle: string;
  minutes: number | null | undefined;
  onChange: (minutes: number) => void;
  courseId?: string;
  /** Stop drag on parent cards */
  onMouseDownStop?: (e: React.MouseEvent) => void;
}

function LastSessionsPopover({
  sessions,
}: {
  sessions: TopicSessionDurationRecord[];
}) {
  return (
    <div
      className="absolute left-0 top-full z-40 mt-1 rounded-lg border border-slate-200 bg-white shadow-lg"
      style={{
        width: 'max(10rem, min(15rem, calc(100vw - 2rem)))',
        maxWidth: 'min(15rem, calc(100% + 3rem))',
      }}
      role="tooltip"
    >
      <p className="m-0 border-b border-slate-100 px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500 leading-none">
        Last 3 sessions
      </p>
      {sessions.length === 0 ? (
        <p className="m-0 px-2.5 py-1.5 text-xs text-slate-500">No prior session data.</p>
      ) : (
        <ul className="m-0 list-none p-0">
          {sessions.map((s, idx) => (
            <li
              key={`${s.session_id || s.sessionLabel}-${s.date}-${idx}`}
              className="m-0 flex items-center justify-between gap-2 border-b border-slate-50 px-2.5 py-1.5 text-xs last:border-b-0"
            >
              <span className="min-w-0 flex-1 truncate text-slate-700 leading-tight">
                {s.date || s.sessionLabel}
              </span>
              <span className="shrink-0 font-semibold text-blue-700 tabular-nums leading-tight">
                {formatDuration(s.actualMinutes)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function TopicDurationField({
  topicId: _topicId,
  topicTitle,
  minutes,
  onChange,
  courseId,
  onMouseDownStop,
}: TopicDurationFieldProps) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [average, setAverage] = useState<number | null>(null);
  const [lastSessions, setLastSessions] = useState<TopicSessionDurationRecord[]>(
    [],
  );
  const [loadingHistory, setLoadingHistory] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  const unset = isTopicDurationUnset(minutes);

  useEffect(() => {
    let cancelled = false;
    const title = String(topicTitle || '').trim();
    if (!title) {
      setAverage(null);
      setLastSessions([]);
      return;
    }
    setLoadingHistory(true);
    void (async () => {
      await prefetchTopicDurationHistory([title], courseId);
      if (cancelled) return;
      setAverage(getTopicAverageDurationMinutes(title));
      setLastSessions(getTopicLastSessionDurations(title, 3));
      setLoadingHistory(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [topicTitle, courseId]);

  useEffect(() => {
    if (!showHistory) return;
    const onDoc = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setShowHistory(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [showHistory]);

  const startEdit = () => {
    setValue(unset ? '' : String(minutes));
    setEditing(true);
    setShowHistory(false);
  };

  const commitFrom = (raw: string) => {
    const parsed = parseInt(raw, 10);
    if (!Number.isNaN(parsed) && parsed >= 1 && parsed <= 300) {
      onChange(parsed);
      setEditing(false);
      return true;
    }
    return false;
  };

  const cancel = () => {
    setEditing(false);
    setValue('');
  };

  const handleBlur = () => {
    if (!commitFrom(value)) {
      cancel();
    }
  };

  const avgTimeChip =
    average != null ? (
      <div
        className="relative inline-flex max-w-full"
        ref={popoverRef}
        onMouseEnter={() => setShowHistory(true)}
        onMouseLeave={() => setShowHistory(false)}
        onFocus={() => setShowHistory(true)}
        onBlur={(e) => {
          if (!popoverRef.current?.contains(e.relatedTarget as Node)) {
            setShowHistory(false);
          }
        }}
      >
        <span
          tabIndex={0}
          className="inline-flex max-w-full cursor-default items-center gap-1 whitespace-nowrap rounded-md bg-blue-50 px-2 py-0.5 font-medium text-blue-700"
          aria-label={`Avg ${formatDuration(average)} from last 3 sessions`}
        >
          <Clock className="h-3 w-3 shrink-0" />
          <span className="truncate">Avg {formatDuration(average)}</span>
        </span>
        {showHistory && <LastSessionsPopover sessions={lastSessions} />}
      </div>
    ) : !loadingHistory ? (
      <span
        className="inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-slate-50 px-2 py-0.5 font-medium text-slate-400"
        style={{ fontSize: 'inherit' }}
      >
        <Clock className="h-3 w-3 shrink-0" />
        <span>Avg --</span>
      </span>
    ) : null;

  if (editing) {
    return (
      <div
        className="flex flex-wrap items-center gap-1.5 text-xs"
        onMouseDown={onMouseDownStop}
        draggable={false}
      >
        <div className="flex flex-wrap items-center gap-1">
          <input
            type="number"
            min={1}
            max={300}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (!commitFrom(value)) cancel();
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                cancel();
              }
            }}
            autoFocus
            placeholder={average != null ? `e.g. ${average}` : 'min'}
            className="w-14 rounded border border-blue-300 px-1.5 py-0.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-200"
            aria-label="Planned duration in minutes"
          />
          <span className="text-xs text-slate-500">min</span>
        </div>
        {avgTimeChip}
      </div>
    );
  }

  if (unset) {
    if (loadingHistory && !avgTimeChip) {
      return (
        <span
          className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs text-slate-400"
          onMouseDown={onMouseDownStop}
        >
          <Clock className="h-3 w-3" />
          …
        </span>
      );
    }
    return (
      <div
        className="flex flex-wrap items-center gap-1.5 text-xs"
        onMouseDown={onMouseDownStop}
      >
        <button
          type="button"
          onClick={startEdit}
          onMouseDown={onMouseDownStop}
          draggable={false}
          className="inline-flex items-center gap-1 rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 font-medium text-amber-800 transition-colors hover:bg-amber-100"
          aria-label="Set planned duration"
        >
          <Pencil className="h-3 w-3" />
          Set time
        </button>
        {avgTimeChip}
      </div>
    );
  }

  const plannedLabel = formatDuration(minutes!);

  return (
    <div className="flex flex-wrap items-center gap-1.5 text-xs">
      <span
        className="inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-slate-100 px-2 py-0.5 font-medium text-slate-700"
        onMouseDown={onMouseDownStop}
      >
        <Clock className="h-3 w-3 text-slate-500" />
        {plannedLabel}
        <button
          type="button"
          onClick={startEdit}
          onMouseDown={onMouseDownStop}
          draggable={false}
          className="rounded p-0.5 text-slate-400 hover:bg-white hover:text-blue-600"
          aria-label="Edit planned duration"
        >
          <Pencil className="h-3 w-3" />
        </button>
      </span>
      {avgTimeChip}
    </div>
  );
}
